/**
 * weitzman-newsletter.js
 * Sends footer / password-page newsletter signups straight to a Klaviyo list.
 *
 * WHY THIS EXISTS
 * Klaviyo's Shopify integration can sync Shopify subscribers on its own, but
 * only when "Sync your Shopify email subscribers" is switched on in Klaviyo
 * with a list selected. That is a setting in another system that nothing in
 * the theme can check, and if it is ever off the form silently goes nowhere.
 * Posting to the list directly makes the signup work on its own terms.
 *
 * The native Shopify post is deliberately left intact underneath. It is what
 * runs with JavaScript off, and it is the recovery path here: if the Klaviyo
 * request fails for any reason, the form is submitted normally so the address
 * still reaches Shopify rather than being dropped. That also keeps the
 * Shopify customer record, which the Klaviyo sync can pick up later as a
 * second route in.
 *
 * Conventions match the rest of the theme: vanilla ES6 in an IIFE, loaded
 * deferred, delegated on document, driven by data-* attributes and a
 * window.__wtz* config object written by the Liquid alongside it.
 */
(function () {
  'use strict';

  if (window.__wtzNewsletterWired) return;
  window.__wtzNewsletterWired = true;

  const config = window.__wtzNewsletterConfig || {};
  if (!config.companyId || !config.listId) return;

  document.addEventListener('submit', function (e) {
    const form = e.target.closest('[data-wtz-newsletter]');
    if (!form) return;

    /* Ignore repeat submits while the first is still in flight. */
    if (form.dataset.wtzSending === '1') {
      e.preventDefault();
      return;
    }

    const input = form.querySelector('input[type="email"]');
    /* Let the browser's own validation handle an empty or malformed address
       rather than posting it anywhere. */
    if (!input || !input.value || !form.checkValidity()) return;

    e.preventDefault();
    form.dataset.wtzSending = '1';

    const btn = form.querySelector('button[type="submit"]');
    const label = btn ? btn.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.textContent = config.sendingLabel || 'Sending…';
    }

    function fallback() {
      /* form.submit() bypasses submit handlers, so this cannot re-enter, but
         clear the flag first so a cancelled navigation does not wedge the
         form. */
      form.dataset.wtzSending = '';
      form.submit();
    }

    function done() {
      form.dataset.wtzSending = '';
      if (btn) {
        btn.textContent = label;
        btn.disabled = false;
      }
      input.value = '';

      const cls = form.dataset.wtzSuccessClass || 'wtz-form-success';
      if (!form.querySelector('.' + cls)) {
        const note = document.createElement('p');
        note.className = cls;
        note.setAttribute('role', 'status');
        note.textContent = config.successLabel || 'Thank you for subscribing!';
        form.appendChild(note);
      }

      /* Tell the promo overlay this visitor has already given us their
         address, so it does not interrupt with the same ask. */
      try {
        localStorage.setItem('wtz_newsletter_signed_up', '1');
      } catch (err) {
        /* private browsing: the overlay keeps its own behavior */
      }
    }

    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(config.companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: '2026-07-15'
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: form.dataset.wtzSource || 'Website',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: input.value,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: config.listId } }
          }
        }
      })
    })
      .then(function (r) {
        /* The endpoint answers 202 Accepted with an empty body. */
        if (r.status === 202 || r.ok) {
          done();
          return;
        }
        fallback();
      })
      .catch(function () {
        /* Offline, blocked by an extension, or CORS: hand the address to
           Shopify's own post rather than losing it. */
        fallback();
      });
  });
})();
